import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
// material
import { alpha, styled, useTheme } from "@material-ui/core/styles";
import { Box, Button, Card, Container, Stack, Typography } from "@material-ui/core";
import {
  WhatsApp as WhatsAppIcon,
  PhoneInTalk as PhoneIcon,
  CheckCircleRounded as CheckIcon
} from "@material-ui/icons";
// components
import Page from "./Page";
import Logo from "./Logo";
import { MotionContainer, varBounceIn, varFadeInUp } from "./animate";
// config
import {
  SUPPORT_CENTER_PHONE_NUMBER,
  SUPPORT_CENTER_PHONE_NUMBER_TEL_URL,
  SUPPORT_CENTER_WHATSAPP_URL
} from "../logic/Consts";
// firebase
import { logout } from "firebaseConfig";

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
  background: `radial-gradient(circle at top ${
    theme.direction === "rtl" ? "right" : "left"
  }, ${alpha(theme.palette.primary.light, 0.16)} 0%, ${theme.palette.background.default} 55%)`
}));

// ----------------------------------------------------------------------

export default function PaidCustomerGate() {
  const { t } = useTranslation();
  const theme = useTheme();

  const benefits = [
    t("paidUserGate.gate.benefitCrm"),
    t("paidUserGate.gate.benefitListings"),
    t("paidUserGate.gate.benefitAi"),
    t("paidUserGate.gate.benefitMore")
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <RootStyle title="Webot Premium">
      <Container maxWidth="md">
        <MotionContainer initial="initial" open>
          <motion.div variants={varBounceIn}>
            <Card
              sx={{
                overflow: "hidden",
                borderRadius: 3,
                boxShadow: (theme) => theme.customShadows.z24
              }}
            >
              <Stack direction={{ xs: "column", md: "row" }}>
                {/* Brand / value panel */}
                <Box
                  sx={{
                    flex: 1,
                    p: { xs: 4, md: 5 },
                    color: "common.white",
                    background: theme.palette.gradients?.primary ?? theme.palette.primary.main,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 3
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        bgcolor: "common.white",
                        borderRadius: 2,
                        p: 0.5,
                        display: "inline-flex"
                      }}
                    >
                      <Logo sx={{ width: 36, height: 36 }} />
                    </Box>
                    <Typography
                      variant="overline"
                      sx={{ letterSpacing: 2, opacity: 0.9 }}
                    >
                      {t("paidUserGate.gate.eyebrow")}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600 }}>
                    {t("paidUserGate.gate.benefitsTitle")}
                  </Typography>

                  <Stack spacing={1.5}>
                    {benefits.map((benefit) => (
                      <Stack key={benefit} direction="row" alignItems="center" spacing={1.5}>
                        <CheckIcon sx={{ fontSize: 22, opacity: 0.95 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {benefit}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* Action panel */}
                <Box
                  sx={{
                    flex: 1,
                    p: { xs: 4, md: 5 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center"
                  }}
                >
                  <motion.div variants={varFadeInUp}>
                    <Typography variant="h4" sx={{ mb: 1.5 }}>
                      {t("paidUserGate.gate.title")}
                    </Typography>
                  </motion.div>

                  <motion.div variants={varFadeInUp}>
                    <Typography sx={{ color: "text.secondary", mb: 4 }}>
                      {t("paidUserGate.gate.subtitle")}
                    </Typography>
                  </motion.div>

                  <motion.div variants={varFadeInUp}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.primary", mb: 1.5 }}
                    >
                      {t("paidUserGate.gate.contactPrompt")}
                    </Typography>
                  </motion.div>

                  <Stack spacing={1.5}>
                    <motion.div variants={varFadeInUp}>
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        href={SUPPORT_CENTER_WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: "#25D366",
                          "&:hover": { bgcolor: "#1EBE5A" }
                        }}
                      >
                        {t("paidUserGate.gate.whatsappCta")}
                      </Button>
                    </motion.div>

                    <motion.div variants={varFadeInUp}>
                      <Button
                        fullWidth
                        size="large"
                        variant="outlined"
                        color="primary"
                        startIcon={<PhoneIcon />}
                        href={SUPPORT_CENTER_PHONE_NUMBER_TEL_URL}
                      >
                        {t("paidUserGate.gate.callCta", { phone: SUPPORT_CENTER_PHONE_NUMBER })}
                      </Button>
                    </motion.div>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mt: 3, display: "block" }}
                  >
                    {t("paidUserGate.gate.footnote")}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={handleLogout}
                      sx={{ color: "text.secondary" }}
                    >
                      {t("paidUserGate.gate.logout")}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </MotionContainer>
      </Container>
    </RootStyle>
  );
}
